import React, { useState, useMemo, useEffect, useContext } from 'react'
import i18n from 'live/services/i18n'
import AdminHeader from 'live/components/header/ui/admin/header'
import styled from 'styled-components'
import Subscriber from 'live/services/subscription'
import PurgeDetails from '../components/PurgeDetails'
import EmptyPurgeData from '../components/EmptyPurgeData'
import api from '../api'
import { FormEnabledContext } from 'live/context/form-enabled'

type PurgeEventMap = {
    [purgeId: string]: PurgeEvent
}

const statusColor = {
    WAITING: 'var(--yellow-700)',
    RUNNING: 'var(--blue-700)',
    COMPLETED: 'var(--green-700)',
    FAILED: 'var(--red-500)',
    CANCELED: 'var(--gray-600)',
    CANCELING: 'var(--gray-700)'
}

const comparePurgeEvents = (
    [, firstPurgeEvent]: [string, PurgeEvent],
    [, secondPurgeEvent]: [string, PurgeEvent]
): number => {
    if (firstPurgeEvent.timestamp < secondPurgeEvent.timestamp) {
        return 1
    }
    if (firstPurgeEvent.timestamp > secondPurgeEvent.timestamp) {
        return -1
    }

    return 0
}

const formatNumber = (num: number): string => {
    return num >= 10 ? '' + num : '0' + num
}

const PurgeHistory = (): JSX.Element => {
    const [purgeTasks, setPurgeTasks] = useState<PurgeEventMap>({})
    const [currentEvent, setCurrentEvent] = useState<PurgeEvent>(null)
    const [showDetails, setShowDetails] = useState(false)

    useEffect(() => {
        const originalTitle = document.title
        document.title = 'Live: ' + i18n('Purge History')

        return () => {
            document.title = originalTitle
        }
    }, [])

    const subscriptionProgress = useMemo(
        () =>
            Subscriber({
                url: '/services/plugin-purge/purge/purge-events/'
            }),
        []
    )

    useEffect(() => {
        subscriptionProgress.on('event', ([event]: PurgeEvent[]) => {
            setPurgeTasks(prevState => {
                const key = event.purgeId
                if (prevState[key]?.finished) return prevState

                const newState = { ...prevState }
                newState[key] = event

                return newState
            })
        })
        subscriptionProgress.flush(null)

        return () => subscriptionProgress.close(null, false)
    }, [subscriptionProgress])

    useEffect(() => {
        setCurrentEvent(purgeTasks[currentEvent?.purgeId])
    }, [purgeTasks, currentEvent])

    const cancelTask = (purgeId: string): void => {
        api.cancelTask(purgeId)
    }

    const context: { readonly: boolean } = useContext(FormEnabledContext)
    const readonly = context.readonly

    return (
        <div className={'admin-content'} style={{ marginLeft: '215px' }}>
            <AdminHeader>
                <h2>
                    <span>{i18n('Purge History')}</span>
                </h2>
            </AdminHeader>
            <div className="admin-body admin-plugin">
                <Container>
                    {Object.keys(purgeTasks).length === 0 ? (
                        <EmptyPurgeData title={i18n('No purges or simulations.')} />
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <td>{i18n('Date')}</td>
                                    <td>{i18n('Type')}</td>
                                    <td>{i18n('Started by')}</td>
                                    <td>{i18n('Status')}</td>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(purgeTasks)
                                    .sort(comparePurgeEvents)
                                    .map(([, purgeEvent], index) => {
                                        const date = new Date(purgeEvent.timestamp)
                                        const dateString =
                                            formatNumber(date.getDate()) +
                                            '/' +
                                            formatNumber(date.getMonth() + 1) +
                                            '/' +
                                            date.getFullYear() +
                                            ' ' +
                                            formatNumber(date.getHours()) +
                                            ':' +
                                            formatNumber(date.getMinutes()) +
                                            ':' +
                                            formatNumber(date.getSeconds())

                                        return (
                                            <TableRow key={index}>
                                                <td>
                                                    <span>{dateString}</span>
                                                </td>
                                                <td>
                                                    <span>{i18n(purgeEvent.simulation ? 'Simulation' : 'Purge')}</span>
                                                </td>
                                                <td>
                                                    <span>{purgeEvent.user}</span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span
                                                            style={{
                                                                textTransform: 'capitalize',
                                                                color: statusColor[purgeEvent.status]
                                                            }}
                                                        >
                                                            {i18n(purgeEvent.status.toLowerCase())}
                                                        </span>
                                                        {!readonly &&
                                                            (purgeEvent.status === 'RUNNING' ||
                                                                purgeEvent.status === 'WAITING') && (
                                                                <a
                                                                    style={{ color: 'var(--red-500)' }}
                                                                    onClick={() => {
                                                                        cancelTask(purgeEvent.purgeId)
                                                                    }}
                                                                >
                                                                    <i className="fa fa-stop" /> {i18n('Cancel')}
                                                                </a>
                                                            )}

                                                        <a
                                                            onClick={() => {
                                                                setCurrentEvent(purgeEvent)
                                                                setShowDetails(true)
                                                            }}
                                                        >
                                                            {i18n('show more')}
                                                        </a>
                                                    </div>
                                                </td>
                                            </TableRow>
                                        )
                                    })}
                            </tbody>
                        </Table>
                    )}
                    {showDetails && (
                        <PurgeDetails currentEvent={currentEvent} closeDetails={() => setShowDetails(false)} />
                    )}
                </Container>
            </div>
        </div>
    )
}

const Container = styled.div`
    margin: 40px 50px 0px 50px;
`

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0px 10px;

    thead {
        td {
            border: none;
            padding: 0px 0px 14px;

            font-weight: 600;
            font-size: 13px;
            line-height: 12px;

            padding-left: 24px;
            text-transform: uppercase;
            color: var(--gray-600);
        }
    }
`

const TableRow = styled.tr`
    td {
        border-width: 1px 0 1px 0;
        border-color: var(--gray-400);
        border-style: solid;
        padding: 14px 24px;
        font-size: 15px;
        line-height: 16px;

        color: var(--gray-800);
        font-weight: 600;

        :first-child {
            width: 24%;
            line-height: 16px;
            border-left-width: 1px;
            border-radius: 4px 0 0 4px;
        }

        :nth-child(2) {
            width: 24%;
            line-height: 16px;
        }

        :nth-child(3) {
            width: 24%;
        }

        :last-child {
            width: 28%;
            border-right-width: 1px;
            border-radius: 0 4px 4px 0;
            a {
                font-size: 13px;
            }
        }
    }
`

export default PurgeHistory
